import { GridBox } from '@components/grid-box';
import { Image } from '@components/image';
import { useConfirmation, useModal } from '@components/modal';
import { Typography, getFormattedDateToNowString } from '@components/typography';
import { Graph, useDeleteGraphMutation } from '@generated/graphql';
import { Stack } from '@mui/material';
import { useMemberLookup } from '@navigation/MemberLookupContext';
import { useNotifications } from '@notifications/Notifications';
import posthog from 'posthog-js';
import { memo } from 'react';
import { GraphsListViewPreviewMenuDownloadConfiguration } from './GraphsListViewPreviewMenuDownloadConfiguration';
import { GraphsListViewPreviewMenuDuplicateConfiguration } from './GraphsListViewPreviewMenuDuplicateConfiguration';
import { GraphsListViewPreviewMenuEditConfiguration } from './GraphsListViewPreviewMenuEditConfiguration';

interface GraphsGridViewPreviewProps {
  graph: Graph;
  onItemClick: (graphId: string) => void;
  refetch: () => void;
}

export const GraphsGridViewPreview = memo(
  ({ graph, onItemClick, refetch }: GraphsGridViewPreviewProps) => {
    const createdByName = useMemberLookup(graph?.createdBy, 'Unknown');
    const [deleteGraph] = useDeleteGraphMutation({
      variables: { workspaceId: graph?.workspaceId, graphId: graph?.graphId },
    });

    const { createAsyncNotification } = useNotifications();
    const { showConfirmation } = useConfirmation({
      onAffirm: createAsyncNotification('Successfully deleted graph.', async () => {
        await deleteGraph();
        refetch();
      }),
      message: `Are you sure you want to delete ${graph?.name ? `${graph.name} ` : ''}graph?`,
    });
    const handleDelete = () => showConfirmation();

    const { showModal: showEditModal } = useModal({
      component: GraphsListViewPreviewMenuEditConfiguration,
      componentProps: { ...graph, refetch },
      modalProps: { title: `Edit ${graph?.name}` },
    });

    const { showModal: showDuplicateModal } = useModal({
      component: GraphsListViewPreviewMenuDuplicateConfiguration,
      componentProps: { ...graph, refetch },
      modalProps: { title: `Save ${graph?.name || 'graph'} copy as` },
    });

    const { showModal: showDownloadModal } = useModal({
      component: GraphsListViewPreviewMenuDownloadConfiguration,
      componentProps: { ...graph },
      modalProps: { title: `Download ${graph?.name}` },
    });

    return (
      <GridBox
        sx={{ height: '100%', width: '100%' }}
        title={graph?.name || '-'}
        description={
          graph?.updatedAt !== graph?.createdAt
            ? getFormattedDateToNowString({ date: graph?.updatedAt, prefix: 'Edited ' })
            : getFormattedDateToNowString({ date: graph?.createdAt, prefix: 'Created ' })
        }
        menuItems={[
          { label: 'Edit', onClick: showEditModal },
          {
            label: 'Duplicate',
            onClick: () => {
              posthog.capture('user_copy-graph_intent');
              showDuplicateModal();
            },
          },
          { label: 'Download', onClick: showDownloadModal },
          { label: 'Delete', onClick: handleDelete },
        ]}
        content={<Image url={graph?.thumbnail} />}
        details={
          <Stack gap={2}>
            <Typography variant="caption2">Creator</Typography>
            <Typography variant="caption1">{createdByName}</Typography>
            <Typography variant="caption2">Description</Typography>
            <Typography variant="caption1" color="white">
              {graph?.description || '-'}
            </Typography>
          </Stack>
        }
        onClick={() => onItemClick(graph?.graphId)}
      />
    );
  },
);
